// Gendac RMIS Acceptance Test Procedure (TST-002-01): the procedures agreed
// with RMIS staff (the original six, plus 8.2, 8.3 and 8.7), described as mock operator screens. Each step is a friendly
// form (what a farmer or operator would key in) plus a function that turns the
// values into the exact RMIS message. Defaults are the ATP fixtures.
//
// The tags are ATP fixtures typed by the operator. AVCS itself never sends
// animal EIDs to RMIS.

export type AtpAction = 'send' | 'receive' | 'group' | 'update' | 'query';
export type AtpVar = 'transferId' | 'onwardTransferId';

/** One animal. `tag2`/`type2` is an optional second new tag on the same animal. */
export type TagRow = { tag: string; type: string; prevTag: string; prevType: string; tag2?: string; type2?: string };

export type FieldValue = string | TagRow[];
export type Values = Record<string, FieldValue>;

export type Field = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'datetime' | 'tags';
  options?: string[];
  default?: FieldValue;
  help?: string;
  /** Only shown under "More details" so the main form stays short. */
  advanced?: boolean;
  /** Tag rows also carry the tag the animal had before. */
  withPrevious?: boolean;
};

export type AtpContext = { gln: string; transferId: string; onwardTransferId: string };

export type AtpStep = {
  id: string;
  title: string;
  kind: 'call' | 'gendac';
  action?: AtpAction;
  /** The button an operator presses. */
  button?: string;
  fields?: Field[];
  /** Builds the RMIS message (or query parameters) from the form. */
  build?: (v: Values, ctx: AtpContext, now: Date) => Record<string, unknown>;
  captures?: AtpVar;
  /** Which carried transfer id an update acts on. */
  transferVar?: AtpVar;
  /** Gendac's confirmation list for a `gendac` step. */
  confirm?: string[];
  note?: string;
};

export type AtpScreen = {
  /** The admin menu key. */
  key: string;
  id: string;
  menuLabel: string;
  title: string;
  intro: string;
  steps: AtpStep[];
};

export const ATP_TAG_TYPES = ['Manual', 'LowFrequency', 'HighFrequency', 'QrCode', 'Undefined'];
export const ATP_SPECIES = ['Cattle', 'Sheep', 'Goats'];
export const ATP_DEFAULT_GLN = '6009238923093';
const GENDAC_GLN = '6009238136417';
const ONWARD_GLN = '6009238972350';

const text = (v: Values, key: string) => String(v[key] ?? '').trim();
const num = (v: Values, key: string) => Number(text(v, key));
const hasValue = (v: Values, key: string) => text(v, key) !== '';
const isoDate = (v: Values, key: string, now: Date) => {
  const raw = text(v, key);
  const parsed = raw ? new Date(raw) : now;
  return (Number.isNaN(parsed.getTime()) ? now : parsed).toISOString();
};
const tagRows = (v: Values, key = 'tags') => ((v[key] as TagRow[]) || []).filter((r) => r.tag.trim());

const livestock = (rows: TagRow[], previousGln: string) =>
  rows.map((r) => {
    const newTags = [{ Tag: r.tag.trim(), TagType: r.type }];
    if (r.tag2?.trim()) newTags.push({ Tag: r.tag2.trim(), TagType: r.type2 || 'Manual' });
    const animal: Record<string, unknown> = { NewTags: newTags };
    if (r.prevTag.trim()) {
      animal.PreviousTagGln = previousGln;
      animal.PreviousTags = [{ Tag: r.prevTag.trim(), TagType: r.prevType }];
    }
    return animal;
  });

const row = (tag: string, type: string, prevTag = '', prevType = 'Manual'): TagRow => ({ tag, type, prevTag, prevType });
/** An animal carrying two new tags. */
const row2 = (tag: string, type: string, tag2: string, type2: string, prevTag = '', prevType = 'Manual'): TagRow => ({
  tag, type, prevTag, prevType, tag2, type2,
});

// ---- reusable step shapes -------------------------------------------------

const sendStep = (o: {
  id: string; title: string; species: string; amount: number; destination: string;
  reference?: string; captures?: AtpVar; lon?: number; lat?: number; note?: string;
}): AtpStep => ({
  id: o.id, title: o.title, kind: 'call', action: 'send', button: 'Send livestock', captures: o.captures, note: o.note,
  fields: [
    { key: 'species', label: 'Species', type: 'select', options: ATP_SPECIES, default: o.species },
    { key: 'amount', label: 'Number of animals sent', type: 'number', default: String(o.amount) },
    { key: 'destination', label: 'Sending to (destination GLN)', type: 'text', default: o.destination },
    { key: 'date', label: 'Date sent', type: 'datetime', default: '' },
    { key: 'reference', label: 'Our reference number (optional)', type: 'text', default: o.reference ?? '' },
    { key: 'lat', label: 'Our latitude', type: 'text', default: String(o.lat ?? 28.27), advanced: true },
    { key: 'lon', label: 'Our longitude', type: 'text', default: String(o.lon ?? 25.74), advanced: true },
  ],
  build: (v, ctx, now) => ({
    RequestingGln: ctx.gln,
    SourceLongitude: num(v, 'lon'),
    SourceLatitude: num(v, 'lat'),
    DestinationGln: text(v, 'destination'),
    TransferDate: isoDate(v, 'date', now),
    SpeciesType: text(v, 'species'),
    ...(hasValue(v, 'reference') ? { ReferenceNumber: text(v, 'reference') } : {}),
    LivestockAmount: num(v, 'amount'),
  }),
});

const receiveStep = (o: {
  id: string; title: string; species: string; expected: number; deceased: number; reference: string;
  captures?: AtpVar; withTransfer?: boolean; withCoordinates?: boolean; note?: string;
}): AtpStep => ({
  id: o.id, title: o.title, kind: 'call', action: 'receive', button: 'Receive livestock', captures: o.captures, note: o.note,
  fields: [
    { key: 'species', label: 'Species', type: 'select', options: ATP_SPECIES, default: o.species },
    { key: 'expected', label: 'Number of animals expected', type: 'number', default: String(o.expected) },
    { key: 'deceased', label: 'Number that died on the way', type: 'number', default: String(o.deceased) },
    { key: 'date', label: 'Date received', type: 'datetime', default: '' },
    { key: 'reference', label: 'Our reference number', type: 'text', default: o.reference },
    { key: 'lat', label: 'Our latitude', type: 'text', default: '18.59', advanced: true },
    { key: 'lon', label: 'Our longitude', type: 'text', default: '33.88', advanced: true },
  ],
  build: (v, ctx, now) => ({
    RequestingGln: ctx.gln,
    ...(o.withTransfer ? { TransferId: ctx.transferId } : {}),
    ReferenceNumber: text(v, 'reference'),
    ...(o.withCoordinates === false ? {} : { DestinationLongitude: num(v, 'lon'), DestinationLatitude: num(v, 'lat') }),
    ReceivedDate: isoDate(v, 'date', now),
    SpeciesType: text(v, 'species'),
    LivestockExpectedCount: num(v, 'expected'),
    LivestockDeceasedCount: num(v, 'deceased'),
  }),
});

/** A group of animals added to a transfer: found by transfer id or by our reference. */
const groupStep = (o: {
  id: string; title: string; tags?: TagRow[]; count?: number; byReference?: string;
  previousGln?: boolean; button?: string; note?: string;
}): AtpStep => ({
  id: o.id, title: o.title, kind: 'call', action: 'group', button: o.button || 'Add animals to the transfer', note: o.note,
  fields: [
    ...(o.byReference !== undefined
      ? [{ key: 'reference', label: 'Reference number of the transfer', type: 'text' as const, default: o.byReference }]
      : []),
    ...(o.tags
      ? [
          ...(o.previousGln ? [{ key: 'previousGln', label: 'Animals came from (previous holder GLN)', type: 'text' as const, default: GENDAC_GLN }] : []),
          { key: 'tags', label: 'Animals in this group', type: 'tags' as const, default: o.tags, withPrevious: Boolean(o.previousGln) },
        ]
      : []),
    ...(!o.tags || o.count !== undefined
      ? [{ key: 'count', label: 'Number of animals in this group', type: 'number' as const, default: String(o.count ?? 2) }]
      : []),
    { key: 'date', label: 'Date processed', type: 'datetime', default: '' },
  ],
  build: (v, ctx, now) => {
    const rows = o.tags ? tagRows(v) : [];
    return {
      RequestingGln: ctx.gln,
      ...(o.byReference !== undefined ? { ReferenceNumber: text(v, 'reference') } : { TransferId: ctx.transferId }),
      LivestockCount: o.tags && !hasValue(v, 'count') ? rows.length : num(v, 'count'),
      ProcessedDate: isoDate(v, 'date', now),
      ...(rows.length ? { Livestock: livestock(rows, text(v, 'previousGln') || GENDAC_GLN) } : {}),
    };
  },
});

const gendacStep = (id: string, title: string, confirm: string[], note?: string): AtpStep => ({
  id, title, kind: 'gendac', confirm, note,
});

const updateSentStep = (id: string): AtpStep => ({
  id, title: 'Correct the transfer', kind: 'call', action: 'update', button: 'Update transfer', transferVar: 'transferId',
  fields: [
    { key: 'sent', label: 'Corrected number sent', type: 'number', default: '15' },
    { key: 'reference', label: 'Corrected reference number', type: 'text', default: 'Reference-8.8-Updated' },
  ],
  build: (v, ctx) => ({
    RequestingGln: ctx.gln,
    LivestockSentCount: num(v, 'sent'),
    ...(hasValue(v, 'reference') ? { ReferenceNumber: text(v, 'reference') } : {}),
  }),
});

const updateReceivedStep = (id: string): AtpStep => ({
  id, title: 'Correct the transfer', kind: 'call', action: 'update', button: 'Update transfer', transferVar: 'transferId',
  fields: [
    { key: 'expected', label: 'Corrected number expected', type: 'number', default: '13' },
    { key: 'deceased', label: 'Corrected number that died', type: 'number', default: '2' },
    { key: 'reference', label: 'Corrected reference number', type: 'text', default: 'Reference-8.9-Updated' },
  ],
  build: (v, ctx) => ({
    RequestingGln: ctx.gln,
    LivestockExpectedCount: num(v, 'expected'),
    LivestockDeceasedCount: num(v, 'deceased'),
    ...(hasValue(v, 'reference') ? { ReferenceNumber: text(v, 'reference') } : {}),
  }),
});

export const ATP_SCREENS: AtpScreen[] = [
  {
    key: 'rmisAtp81', id: '8.1', menuLabel: '8.1 Send livestock',
    title: 'Send livestock (no reference number)',
    intro: 'The farmer sends cattle to another location. One transfer, then two groups: the first with no animals listed, the second with two tagged animals.',
    steps: [
      sendStep({ id: '8.1-1', title: 'Send the livestock', species: 'Cattle', amount: 10, destination: GENDAC_GLN, captures: 'transferId' }),
      groupStep({ id: '8.1-2', title: 'First group — numbers only', count: 2 }),
      groupStep({ id: '8.1-3', title: 'Second group — two tagged animals', tags: [row('tag-8.1-1', 'Manual'), row('tag-8.1-2', 'QrCode')] }),
      gendacStep('8.1-4', 'Gendac checks their system', [
        'Source and destination reference numbers are empty',
        'Source GLN is ours, destination GLN is 6009238136417, received date is empty',
        'Species Cattle, sent count 10, expected count empty, status Sent',
        'Source tags tag-8.1-1 (Manual) and tag-8.1-2 (QrCode); destination livestock empty',
      ]),
    ],
  },
  {
    key: 'rmisAtp82', id: '8.2', menuLabel: '8.2 Send (with reference)',
    title: 'Send livestock (with a reference number)',
    intro: 'The farmer sends cattle with their own reference number. The two tagged animals are then added by that reference number, not by transfer id.',
    steps: [
      sendStep({ id: '8.2-1', title: 'Send the livestock', species: 'Cattle', amount: 10, destination: GENDAC_GLN, reference: 'Reference-8.2' }),
      groupStep({
        id: '8.2-2', title: 'Add two tagged animals by reference number', byReference: 'Reference-8.2',
        tags: [row('tag-8.2-1', 'HighFrequency'), row('tag-8.2-2', 'LowFrequency')],
      }),
      gendacStep('8.2-3', 'Gendac checks their system', [
        'Source reference number Reference-8.2; destination reference number empty',
        'Source GLN is ours, destination GLN is 6009238136417, received date is empty',
        'Species Cattle, sent count 10, expected count empty, status Sent',
        'Source tags tag-8.2-1 (HighFrequency) and tag-8.2-2 (LowFrequency); destination livestock empty',
      ]),
    ],
  },
  {
    key: 'rmisAtp83', id: '8.3', menuLabel: '8.3 Send (multiple tags)',
    title: 'Send livestock with multiple tags',
    intro: 'The farmer sends cattle, and one animal in the group carries two tags at once.',
    steps: [
      sendStep({ id: '8.3-1', title: 'Send the livestock', species: 'Cattle', amount: 10, destination: GENDAC_GLN, captures: 'transferId' }),
      {
        ...groupStep({
          id: '8.3-2', title: 'Add one animal with two tags',
          tags: [row2('tag-8.3-1', 'Manual', 'tag-8.3-2', 'Manual')], count: 2,
          note: 'As the ATP is written, the group count is 2 although one animal is listed. Change the count if Gendac says otherwise.',
        }),
      },
      gendacStep('8.3-3', 'Gendac checks their system', [
        'Source and destination reference numbers empty, received date empty',
        'Source GLN is ours, destination GLN is 6009238136417',
        'Species Cattle, sent count 10, expected count empty, status Sent',
        'Animal 1 has two tags: tag-8.3-1 (Manual) and tag-8.3-2 (Manual); destination livestock empty',
      ]),
    ],
  },
  {
    key: 'rmisAtp84', id: '8.4', menuLabel: '8.4 Receive, then send on',
    title: 'Receive an existing transfer and send it on',
    intro: 'Gendac sends animals to the farm. The farmer receives and re-tags them, then sends them on to the next location carrying the tags just applied.',
    steps: [
      gendacStep('8.4-1', 'Gendac creates and sends the inbound transfer', ['Gendac has sent the transfer to our GLN'],
        'Gendac sends 15 Sheep with reference Reference-8.4, plus a group of two animals tagged gendac-tag-8.4-1 (Manual) and gendac-tag-8.4-2 (LowFrequency).'),
      {
        id: '8.4-2', title: 'Look for what Gendac sent us', kind: 'call', action: 'query', button: 'Find transfers', captures: 'transferId',
        note: 'Pick the transfer whose source reference number is Reference-8.4.',
        fields: [{ key: 'source', label: 'Sent by (their GLN)', type: 'text', default: GENDAC_GLN }],
        build: (v, ctx) => ({ RequestingGln: ctx.gln, SourceGln: text(v, 'source') }),
      },
      receiveStep({ id: '8.4-3', title: 'Receive the animals', species: 'Sheep', expected: 15, deceased: 2, reference: 'Dest-Reference-8.4', withTransfer: true }),
      groupStep({
        id: '8.4-4', title: 'Record the animals received and re-tag them', byReference: 'Dest-Reference-8.4', previousGln: true,
        tags: [
          row('tag-8.4-1', 'HighFrequency', 'gendac-tag-8.4-1', 'Manual'),
          row('tag-8.4-2', 'HighFrequency', 'gendac-tag-8.4-2', 'LowFrequency'),
          row('tag-8.4-3', 'HighFrequency'),
        ],
      }),
      gendacStep('8.4-5', 'Gendac checks the receive', [
        'Animals 1 and 2 share one LivestockId across source and destination tags',
        'Animal 3 exists only at the destination',
        'Traceability report on tag-8.4-1 shows one Sheep entry from 6009238136417 to our GLN',
      ]),
      sendStep({
        id: '8.4-6', title: 'Send the animals on', species: 'Sheep', amount: 10, destination: ONWARD_GLN,
        reference: 'Reference-8.4-2', captures: 'onwardTransferId', lon: 33.88, lat: 18.59,
      }),
      groupStep({
        id: '8.4-7', title: 'List the animals sent on', byReference: 'Reference-8.4-2',
        tags: [row('tag-8.4-1', 'HighFrequency'), row('tag-8.4-2', 'HighFrequency'), row('tag-8.4-3', 'HighFrequency')],
      }),
      gendacStep('8.4-8', 'Gendac checks the onward transfer', [
        'The onward transfer is Sent with all three tags',
        'tag-8.4-1 also carries gendac-tag-8.4-1 with SupplierGln 6009238136417',
        'The traceability report shows two hops, the second with no receive date',
      ]),
    ],
  },
  {
    key: 'rmisAtp85', id: '8.5', menuLabel: '8.5 Receive (new animals)',
    title: 'Receive a new transfer (no previous holder)',
    intro: 'The farmer receives animals that come with no previous record. Expect the transfer to read Delivered.',
    steps: [
      receiveStep({ id: '8.5-1', title: 'Receive the animals', species: 'Goats', expected: 10, deceased: 2, reference: 'Reference-8.5' }),
      groupStep({ id: '8.5-2', title: 'Record two new animals', byReference: 'Reference-8.5', tags: [row('tag-8.5-1', 'Manual'), row('tag-8.5-2', 'LowFrequency')] }),
      gendacStep('8.5-3', 'Gendac checks their system', [
        'Source GLN and transfer date are empty; destination is our GLN',
        'Species Goats, expected 10, deceased 2, status Delivered',
        'Both tags at the destination with SupplierGln our GLN; source livestock empty',
        'One traceability entry, from no location to our GLN',
      ]),
    ],
  },
  {
    key: 'rmisAtp86', id: '8.6', menuLabel: '8.6 Receive (with previous GLN)',
    title: 'Receive a new transfer (with a previous holder)',
    intro: 'As 8.5, but the animals arrive carrying tags from Gendac, so each keeps its old tag beside the new one.',
    steps: [
      receiveStep({ id: '8.6-1', title: 'Receive the animals', species: 'Cattle', expected: 5, deceased: 1, reference: 'Reference-8.6' }),
      groupStep({
        id: '8.6-2', title: 'Record two animals with their previous tags', byReference: 'Reference-8.6', previousGln: true,
        tags: [row('tag-8.6-1', 'Manual', 'gendac-tag-8.6-1', 'Manual'), row('tag-8.6-2', 'HighFrequency', 'gendac-tag-8.6-2', 'LowFrequency')],
        note: 'The ATP text lists animal 2’s tag as LowFrequency in one place and HighFrequency in two. HighFrequency is used; raise it in the workshop.',
      }),
      gendacStep('8.6-3', 'Gendac checks their system', [
        'Status Delivered',
        'Each animal carries its new tag (SupplierGln our GLN) and its previous tag (SupplierGln 6009238136417)',
      ]),
    ],
  },
  {
    key: 'rmisAtp87', id: '8.7', menuLabel: '8.7 Receive (multiple tags)',
    title: 'Receive an existing transfer — animal with multiple tags',
    intro: 'Gendac sends one animal carrying two tags. The farmer finds the transfer, receives it, and records the animal with its previous tag and two new tags.',
    steps: [
      gendacStep('8.7-1', 'Gendac creates and sends the inbound transfer', ['Gendac has sent the transfer to our GLN'],
        'Gendac sends 10 Cattle with reference Reference-8.7, plus a group of one animal carrying two new tags: gendac-tag-8.7-1 (Manual) and gendac-tag-8.7-2 (LowFrequency).'),
      {
        id: '8.7-2', title: 'Look for what Gendac sent us', kind: 'call', action: 'query', button: 'Find transfers', captures: 'transferId',
        note: 'Pick the transfer whose source reference number is Reference-8.7.',
        fields: [
          { key: 'source', label: 'Sent by (their GLN)', type: 'text', default: GENDAC_GLN },
          { key: 'status', label: 'Status', type: 'select', options: ['Sent', 'Delivered'], default: 'Sent' },
        ],
        build: (v, ctx) => ({ RequestingGln: ctx.gln, SourceGln: text(v, 'source'), TransferStatus: text(v, 'status') }),
      },
      receiveStep({ id: '8.7-3', title: 'Receive the animals', species: 'Cattle', expected: 10, deceased: 0, reference: 'Reference-8.7', withTransfer: true }),
      groupStep({
        id: '8.7-4', title: 'Record the animal with its previous tag and two new tags', byReference: 'Reference-8.7', previousGln: true,
        tags: [row2('tag-8.7-1', 'LowFrequency', 'tag-8.7-2', 'HighFrequency', 'gendac-tag-8.7-1', 'Manual')],
      }),
      gendacStep('8.7-5', 'Gendac checks the animal’s tags', [
        'One Cattle animal with four tags',
        'gendac-tag-8.7-1 (Manual) and gendac-tag-8.7-2 (LowFrequency), SupplierGln 6009238136417',
        'tag-8.7-1 (LowFrequency) and tag-8.7-2 (HighFrequency), SupplierGln our GLN',
        'Traceability report on tag-8.7-1: Cattle, a single entry from 6009238136417 to our GLN',
      ]),
    ],
  },
  {
    key: 'rmisAtp88', id: '8.8', menuLabel: '8.8 Correct a sent transfer',
    title: 'Update a sent transfer',
    intro: 'The farmer sent 8 sheep, then realises it was 15 and changes the reference number.',
    steps: [
      sendStep({ id: '8.8-1', title: 'Send the livestock', species: 'Sheep', amount: 8, destination: GENDAC_GLN, reference: 'Reference-8.8', captures: 'transferId' }),
      gendacStep('8.8-2', 'Gendac checks the original', ['Sent amount 8', 'Source reference number Reference-8.8']),
      updateSentStep('8.8-3'),
      gendacStep('8.8-4', 'Gendac checks the correction', ['Sent count 15', 'Source reference number Reference-8.8-Updated']),
    ],
  },
  {
    key: 'rmisAtp89', id: '8.9', menuLabel: '8.9 Correct a received transfer',
    title: 'Update a received transfer',
    intro: 'The farmer received animals, then corrects how many were expected and how many died, and the reference number.',
    steps: [
      receiveStep({
        id: '8.9-1', title: 'Receive the animals', species: 'Cattle', expected: 14, deceased: 1, reference: 'Reference-8.9',
        captures: 'transferId', withCoordinates: false,
        note: 'The ATP leaves out our coordinates here. If RMIS refuses the message, add them under More details and tell Gendac.',
      }),
      gendacStep('8.9-2', 'Gendac checks the original', ['Expected 14, deceased 1', 'Destination reference number Reference-8.9']),
      updateReceivedStep('8.9-3'),
      gendacStep('8.9-4', 'Gendac checks the correction', ['Expected 13, deceased 2', 'Destination reference number Reference-8.9-Updated']),
    ],
  },
];

export const ATP_CONNECT_KEY = 'rmisAtpConnect';
export const ATP_KEYS = [ATP_CONNECT_KEY, ...ATP_SCREENS.map((s) => s.key)];

/** Starting values for every field of a step. */
export function defaultValues(step: AtpStep): Values {
  const out: Values = {};
  for (const f of step.fields || []) {
    out[f.key] = Array.isArray(f.default) ? f.default.map((r) => ({ ...r })) : f.default ?? '';
  }
  return out;
}

/** Transfer id out of an RMIS response, whatever the nesting. */
export function extractTransferId(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  if (Array.isArray(data)) {
    for (const item of data) {
      const found = extractTransferId(item);
      if (found) return found;
    }
    return '';
  }
  const record = data as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (key.toLowerCase() === 'transferid' && typeof value === 'string' && value) return value;
  }
  for (const value of Object.values(record)) {
    if (value && typeof value === 'object') {
      const found = extractTransferId(value);
      if (found) return found;
    }
  }
  return '';
}
